import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import toast from "react-hot-toast";
import FormRenderer from "./FormRenderer";
import { usePatientData } from "../../commonFunction/storage/Patientdatacontext";
import { getCaseTypeNameById } from "../../helpers/Util";

// ── SessionStorage helpers ────────────────────────────────────────────────────
function getTokenData() {
  try { return JSON.parse(sessionStorage.getItem("tokenData") || "{}"); } catch { return {}; }
}
function getSelectedRow() {
  try {
    // Login.js stores parsed selectedRow at its own key (line 181)
    const direct = sessionStorage.getItem("selectedRow");
    if (direct) return JSON.parse(direct);
    // Fallback: it's a JSON string inside tokenData
    const td = getTokenData();
    if (td.selectedRow) return JSON.parse(td.selectedRow);
    return {};
  } catch { return {}; }
}
function getCommonServiceToken() {
  try {
    const raw = sessionStorage.getItem("commonServiceToken");
    if (!raw) return "";
    const parsed = JSON.parse(raw);
    return typeof parsed === "string" ? parsed : "";
  } catch { return ""; }
}
function getAccessApiToken() {
  try {
    const raw = sessionStorage.getItem("accessApiToken");
    if (!raw) return "";
    const parsed = JSON.parse(raw);
    if (typeof parsed === "string") return parsed;
    return parsed?.Token || parsed?.AccessToken || parsed?.token || "";
  } catch { return ""; }
}

// ── ENV header — patient app has no interceptor ───────────────────────────────
function getEnv() {
  const h = window.location.hostname;
  return (h === "localhost" || h.startsWith("dev") || h.startsWith("127.")) ? "UAT" : "PROD";
}
function buildHeaders(token) {
  return { Accept: "application/json", "Content-Type": "application/json", Authorization: `Bearer ${token}`, ENV: getEnv() };
}

// ── viewSign — mirrors Intakesheetwrapper viewSign ────────────────────────────
async function viewSign(signPathResult) {
  try {
    const row = getSelectedRow();
    const commonToken = getCommonServiceToken();
    const commonUrl   = row?.object?.commonServiceUrl;
    if (!signPathResult || typeof signPathResult !== "string" || signPathResult.trim() === "") return "";
    const fileRes = await fetch(
      `${commonUrl}api/CommonServiceAPI/v1/Document/GetFile?${signPathResult}`,
      { method: "GET", headers: { Accept: "blob", "Content-Type": "application/json", Authorization: `Bearer ${commonToken}`, ENV: getEnv() } }
    );
    if (!fileRes.ok) return "";
    const blob = await fileRes.blob();
    if (!blob || blob.size === 0) return "";
    return await new Promise(resolve => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(`data:image/png;base64,${reader.result.split(",")[1]}`);
      reader.readAsDataURL(blob);
    });
  } catch { return ""; }
}

// ── Module-level caches & in-flight promises to eliminate duplicate API calls ──
const schemaCache = new Map();
const schemaPromises = new Map();

const savedPromises = new Map();

const providerSignCache = new Map();
const providerSignPromises = new Map();

const prefillCache = new Map();
const prefillPromises = new Map();

// ── getProviderSign — mirrors Intakesheetwrapper getProviderSign ──────────────
async function getProviderSign(providerId, companyId) {
  if (!providerId) return "";
  const cacheKey = `${providerId}::${companyId || ""}`;
  if (providerSignCache.has(cacheKey)) {
    return providerSignCache.get(cacheKey);
  }
  if (providerSignPromises.has(cacheKey)) {
    return await providerSignPromises.get(cacheKey);
  }

  const promise = (async () => {
    try {
      const row = getSelectedRow();
      const accessToken   = getAccessApiToken();
      const doctorBaseUrl = row?.object?.doctorBaseUrl;
      if (!accessToken || !doctorBaseUrl) return "";
      const res = await fetch(
        `${doctorBaseUrl}ProviderMaster/GetSignPath?id=${providerId}&CompanyId=${companyId}`,
        { method: "GET", headers: { Accept: "application/json", Authorization: `Bearer ${accessToken}`, ENV: getEnv() } }
      );
      if (!res.ok) return "";
      const result = await res.json();
      if (!result || result === "" || result.HasException) return "";
      const signBase64 = await viewSign(result);
      providerSignCache.set(cacheKey, signBase64);
      return signBase64;
    } catch (e) {
      console.error("[DynamicIntakeTab] getProviderSign error:", e);
      return "";
    } finally {
      providerSignPromises.delete(cacheKey);
    }
  })();

  providerSignPromises.set(cacheKey, promise);
  return await promise;
}

// ── buildPrefillData — mirrors Intakesheetwrapper fetchPatient exactly ────────
async function buildPrefillData(caseType = '') {
  const td  = getTokenData();
  const row = getSelectedRow();
  const obj = row?.object || {};

  const companyId = td.companyId || obj.accountID || '';
  const caseId    = td.caseId    || obj.caseNo    || '';
  const cacheKey  = `${caseId}::${companyId}`;

  if (prefillCache.has(cacheKey)) {
    const cached = prefillCache.get(cacheKey);
    return caseType ? { ...cached, case_type: caseType } : cached;
  }

  if (prefillPromises.has(cacheKey)) {
    const data = await prefillPromises.get(cacheKey);
    return caseType ? { ...data, case_type: caseType } : data;
  }

  const promise = (async () => {
    try {
      const accessToken     = getAccessApiToken();
      const commonToken     = getCommonServiceToken();
      const doctorBaseUrl   = obj.doctorBaseUrl      || '';
      const caseAndVisitUrl = obj.caseAndVisitUrl    || '';
      const userUID         = obj.userUID            || '';
      const patientId       = obj.patientID          || '';
      const providerId      = td.providerId          || '';

      // ── Mirrors sd (GetSessionData) and token (GetTokenInfo) from Intakesheetwrapper ──
      const sd = {
        ID:        td.userId               || '',
        UserName:  td.userName             || '',
        Domain:    obj?.domain,
        UserUID:   userUID,
        Account:   { ID: companyId },
        Specialty: { Name: td.doctorSpecialtyName || '', Code: obj?.specialityCode, ID: td.doctorSpecialtyId || '' },
        Physician: { ID: td.doctorId || '', ProviderId: td.providerId || '' },
        ApiTokenv1: { Token: commonToken },
      };
      const callingApp = 'PatientPortal';

      const toDateInput = (raw) => {
        if (!raw) return '';
        const parts = raw.split('/');
        if (parts.length === 3) return `${parts[2]}-${parts[0].padStart(2,'0')}-${parts[1].padStart(2,'0')}`;
        return raw;
      };

      const getCurrentDate = () => {
        const d = new Date();
        return `${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')}/${d.getFullYear()}`;
      };

      // ── Provider sign ─────────────────────────────────────────────────────────
      const providerSignBase64 = providerId ? await getProviderSign(providerId, companyId) : '';

      // ── GetPatientInfo — exact mirror of Intakesheetwrapper line 780-907 ─────
      let get = () => '';
      let p = null;
      try {
        const res = await fetch(`${doctorBaseUrl}Patient/GetPatientInfo`, {
          method: 'POST',
          headers: {
            Accept:         'application/json',
            'Content-Type': 'application/json',
            Authorization:  'Bearer ' + accessToken,
            CallingApp:     callingApp,
          },
          body: JSON.stringify({
            User: {
              Specialty: sd.Specialty, ID: sd.ID, Domain: sd.Domain,
              UserName:  sd.UserName,  Account: { ID: sd.Account.ID },
              Physician: sd.Physician, Token: accessToken,
            },
            Patient: { CaseID: caseId, ID: patientId },
          }),
        });
        const result = await res.json();
        p = result.Data?.[0]?.PersonalInfo;
        const arr = Array.isArray(p?.[0]) ? p[0] : (Array.isArray(p) ? p : []);
        get = (name) => arr.find(x => x.Name === name)?.Value || '';
      } catch (e) { console.error('[DynamicIntakeTab] GetPatientInfo failed:', e); }

      const getMaritalStatusText    = () => ({ '1':'Single','2':'Married','3':'Other' })[get('rdbMaritalStatus')] || '';
      const getEmergencyContactName = () => [get('txtFirstNameEC'), get('txtMiddleNameEC'), get('txtLastNameEC')].filter(Boolean).join(' ');
      const getAttorneyName         = () => [get('txtAttorneyFName'), get('txtAttorneyLName')].filter(Boolean).join(' ');

      // ── ProviderDDL — mirrors Intakesheetwrapper line 819-827 ────────────────
      let providerListOptions = [];
      try {
        const plRes = await fetch(
          `${caseAndVisitUrl}CaseAndVisitAPI/v1/Provider/ProviderDDL?UserUid=${sd.UserUID}&ApplicationId=7&CompanyId=${sd.Account.ID}&UpdatedBy=US00001`,
          { method: 'GET', headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: 'Bearer ' + sd.ApiTokenv1.Token, CallingApp: callingApp } }
        );
        const plResult = await plRes.json();
        if (plResult.responseCode === 200 && Array.isArray(plResult.data)) {
          providerListOptions = plResult.data.map(item => ({ key: item.key, value: item.value }));
        }
      } catch (e) { console.error('[DynamicIntakeTab] ProviderDDL failed:', e); }

      // ── GetTechniqueDetails — mirrors Intakesheetwrapper line 835-868 ─────────
      let proceduresListOptions = [];
      try {
        const techRes = await fetch(`${doctorBaseUrl}GenericAppointment/GetTechniqueDetails`, {
          method: 'POST',
          headers: {
            Accept:         'application/json',
            'Content-Type': 'application/json',
            Authorization:  'Bearer ' + accessToken,
            CallingApp:     callingApp,
          },
          body: JSON.stringify({
            User: {
              Account:   { ID: sd.Account.ID },
              Username:  sd.UserName        || '',
              Specialty: { Name: sd.Specialty?.Name || '', Code: sd.Specialty?.Code || '', ID: sd.Specialty?.ID || '' },
              Token:     accessToken,
              ID:        sd.ID              || '',
              Physician: { ID: sd.Physician?.ID || '' },
              Domain:    sd.Domain          || '',
            },
            CaseID:    caseId,
            UserID:    sd.ID     || '',
            CompanyID: sd.Account.ID,
          }),
        });
        const techResult = await techRes.json();
        if (!techResult.HasException && Array.isArray(techResult.Data?.[0])) {
          proceduresListOptions = techResult.Data[0]
            .filter(t => t.IsActive !== false)
            .map(t => ({ key: String(t.Id), value: t.TechniqueName }));
        }
      } catch (e) { console.error('[DynamicIntakeTab] GetTechniqueDetails failed:', e); }

      // ── GetState — resolve state code → state name (mirrors Intakesheetwrapper) ─
      let statesData = [{ ID: '', Code: '', Name: 'Select' }];
      try {
        const statesRes = await fetch(`${doctorBaseUrl}Patient/State`, {
          method: 'POST',
          headers: {
            Accept:         'application/json',
            'Content-Type': 'application/json',
            Authorization:  'Bearer ' + accessToken,
            CallingApp:     callingApp,
          },
          body: JSON.stringify({
            User: {
              Specialty: sd.Specialty, ID: sd.ID, Domain: sd.Domain,
              UserName:  sd.UserName,  Account: { ID: sd.Account.ID },
              Physician: sd.Physician, Token: accessToken,
            },
          }),
        });
        const statesResult = await statesRes.json();
        if (statesResult !== '' && !statesResult.HasException) {
          statesData = [{ ID: '', Code: '', Name: 'Select' }, ...(statesResult.Data?.[0]?.Result || [])];
        }
      } catch (e) { console.error('[DynamicIntakeTab] GetState failed:', e); }

      // ── Provider details (name / address / email / phone / fax) ──────────────
      let providerNameFromApi    = td.providerName || '';
      let providerAddressFromApi = '';
      let providerEmailFromApi   = '';
      let providerPhoneFromApi   = '';
      let providerFaxFromApi     = '';
      let providerDropdownKey    = providerId;
      try {
        const pvRes = await fetch(
          `${doctorBaseUrl}ProviderDocument/GetProviderListWithoutAddr?companyId=${sd.Account.ID}`,
          { method: 'GET', headers: { Authorization: 'Bearer ' + accessToken, CallingApp: callingApp } }
        );
        const pvResult = await pvRes.json();
        if (Array.isArray(pvResult) && !pvResult.HasException) {
          const matchedProvider = pvResult.find(pv => pv.ProviderId === providerId)
            || pvResult.find(pv => pv.ProviderName === providerNameFromApi)
            || pvResult[0];
          if (matchedProvider) {
            providerNameFromApi    = matchedProvider.ProviderName || providerNameFromApi;
            providerAddressFromApi = [matchedProvider.Address, matchedProvider.City, matchedProvider.State, matchedProvider.Zip].filter(Boolean).join(', ');
            providerEmailFromApi   = matchedProvider.Email || '';
            providerPhoneFromApi   = matchedProvider.Phone || '';
            providerFaxFromApi     = matchedProvider.Fax   || '';
            const ddlMatch = providerListOptions.find(o => o.key === providerId)
              || providerListOptions.find(o => o.value?.toLowerCase().includes(matchedProvider.ProviderName?.toLowerCase()));
            providerDropdownKey = ddlMatch?.key || providerId;
          }
        }
      } catch (e) { console.error('[DynamicIntakeTab] GetProviderListWithoutAddr failed:', e); }

      // ── prefillData — exact mirror of Intakesheetwrapper setPatientPrefillData ──
      // case_type is left empty in the cached copy; the caller's caseType is applied below.
      const data = {
        txtPatientFName:            obj.patientName         || '',
        patient_name:               obj.patientName         || '',
        patient_firstname:          obj.patientFirstName || get('txtPatientFirstName') || (obj.patientName||'').split(' ')[0] || '',
        patient_lastname:           obj.patientLastName  || get('txtPatientLastName')  || (obj.patientName||'').split(' ').slice(1).join(' ') || '',
        txtPatientAddress:          get('txtPatientAddress') || obj.patientAddress || '',
        mailing_Address:            get('txtPatientAddress') || obj.patientAddress || '',
        txtDateOfBirth:             toDateInput(obj.patientDOB || ''),
        txtSocialSecurityNumber:    get('txtSocialSecurityNumber') || obj.patientSSN || '',
        txtDateofAccident:          obj.patientDOA           || '',
        txtDateOfAccident:          toDateInput(obj.patientDOA || ''),
        txtClaimNumber:             get('txtClaimNumber')    || obj.claimNumber || '',
        txtPatientPhone:            get('txtPatientPhone')   || obj.patientPhone || '',
        patient_city:               get('txtPatientCity')    || '',
        patient_state:              statesData.find(x => x.Code === get('extddlPatientState'))?.Name || get('txtPatientState') || '',
        patient_zip:                get('txtPatientZip')     || '',
        patient_age:                get('txtAge')            || '',
        patient_sex:                get('ddlSex')            || '',
        patient_maritalstatus:      getMaritalStatusText(),
        case_type:                  '',
        case_no:                    obj.caseNo               || caseId,
        carrier:                    obj.carrierName          || '',
        insurance_name:             get('txtInsuranceName')  || '',
        insured_name:               get('txtPolicyHolder')   || '',
        insurance_company_address:  get('txtInsuranceAddress') || '',
        adjuster_phone:             get('txtAdjusterPhone')  || '',
        emergency_contact_name:     getEmergencyContactName(),
        emergency_phone:            get('txtcellPhone')      || '',
        policy_number:              get('txtPolicyNumber')   || '',
        wcb:                        get('txtWCBNumber')      || '',
        employ_name:                get('txtEmployerName')   || '',
        attorney_name:              getAttorneyName(),
        attorney_phone:             get('txtAttorneyPhone')  || '',
        current_date:               getCurrentDate(),
        doctor_name:                td.doctorName            || '',
        company_name:               td.companyName           || '',
        provider_name:              providerNameFromApi,
        provider_address:           providerAddressFromApi,
        provider_email:             providerEmailFromApi,
        provider_phone:             providerPhoneFromApi,
        provider_fax:               providerFaxFromApi,
        provider_id:                providerDropdownKey      || providerId,
        provider_sign:              providerSignBase64,
        provider_list:              providerListOptions,
        procedures_list:            proceduresListOptions,
      };

      prefillCache.set(cacheKey, data);
      return data;
    } catch (e) {
      console.error('[DynamicIntakeTab] buildPrefillData error:', e);
      return {};
    } finally {
      prefillPromises.delete(cacheKey);
    }
  })();

  prefillPromises.set(cacheKey, promise);
  const data = await promise;
  return caseType ? { ...data, case_type: caseType } : data;
}

// ── Parse saved formData → flat { fieldId: value } ───────────────────────────
function parseSavedFormData(formDataJson) {
  try {
    const parsed = JSON.parse(formDataJson);
    const map = {};
    (parsed.fields || []).forEach(f => {
      if (f.value !== null && f.value !== undefined) map[f.id] = f.value;
      if (f.segments) {
        const seg = {};
        let has = false;
        f.segments.forEach(s => { if (s.value !== null && s.value !== undefined) { seg[s.id] = s.value; has = true; } });
        if (has) map[f.id] = { ...(map[f.id] || {}), ...seg };
      }
    });
    return map;
  } catch { return {}; }
}

// ── Build formData for save ───────────────────────────────────────────────────
function buildFormDataObj(schema, responses) {
  let fields = [];
  try { fields = JSON.parse(schema?.schemaJson || "{}").fields || []; } catch {}
  return {
    version: 1,
    fields: fields.map(f => {
      const entry = { id: f.id, type: f.type, value: responses[f.id] ?? null };
      if (f.type === "inline_row" && f.segments) {
        entry.segments = f.segments.map(s => ({
          id: s.id, segType: s.segType, value: (responses[f.id] || {})[s.id] ?? null,
        }));
      }
      return entry;
    }),
  };
}

// ── API calls ─────────────────────────────────────────────────────────────────
async function apiFetchSchema(baseUrl, formTemplateId, companyId, token) {
  const cacheKey = `${baseUrl}::${formTemplateId}::${companyId}`;
  if (schemaCache.has(cacheKey)) {
    return schemaCache.get(cacheKey);
  }
  if (schemaPromises.has(cacheKey)) {
    return await schemaPromises.get(cacheKey);
  }

  const promise = (async () => {
    try {
      const res = await fetch(
        `${baseUrl}api/CommonServiceAPI/v1/FormTemplate/GetFormTemplateById?formTemplateId=${formTemplateId}&companyId=${companyId}`,
        { method: "GET", headers: buildHeaders(token) }
      );
      const result = await res.json();
      if (result.responseCode === 200) {
        const data = result.data || result.Data || null;
        if (data) {
          const formatted = { ...data, schemaJson: data.schemaJson || data.schemaJsonPath };
          schemaCache.set(cacheKey, formatted);
          return formatted;
        }
      }
      return null;
    } catch (e) {
      console.error("[DynamicIntakeTab] apiFetchSchema error:", e);
      return null;
    } finally {
      schemaPromises.delete(cacheKey);
    }
  })();

  schemaPromises.set(cacheKey, promise);
  return await promise;
}

async function apiFetchSaved(baseUrl, formTemplateId, caseId, companyId, token) {
  const cacheKey = `${baseUrl}::${formTemplateId}::${caseId}::${companyId}`;
  if (savedPromises.has(cacheKey)) {
    return await savedPromises.get(cacheKey);
  }

  const promise = (async () => {
    try {
      const res = await fetch(
        `${baseUrl}api/CommonServiceAPI/v1/FormTemplate/GetFormTemplateByCaseId?formTemplateId=${formTemplateId}&caseId=${caseId}&companyId=${companyId}`,
        { method: "GET", headers: buildHeaders(token) }
      );
      const result = await res.json();
      if (result.responseCode === 200 && result.data?.formData) {
        return parseSavedFormData(result.data.formData);
      }
    } catch(e) {
      console.error("[DynamicIntakeTab] apiFetchSaved error:", e);
    } finally {
      savedPromises.delete(cacheKey);
    }
    return {};
  })();

  savedPromises.set(cacheKey, promise);
  return await promise;
}

async function apiSave(baseUrl, token, formTemplateId, caseId, companyId, userId, formName, formDataObj) {
  const res = await fetch(
    `${baseUrl}api/CommonServiceAPI/v1/FormTemplate/SaveFormTemplateData`,
    {
      method: "POST", headers: buildHeaders(token),
      body: JSON.stringify({
        FormTemplateId: formTemplateId, CaseId: String(caseId),
        FormData: JSON.stringify(formDataObj), UserId: userId,
        CompanyId: companyId, FormName: formName,
      }),
    }
  );
  const result = await res.json();
  return result?.responseCode === 200;
}

// ── Component ─────────────────────────────────────────────────────────────────
const DynamicIntakeTab = forwardRef(function DynamicIntakeTab({ tabId, onReady, copySignature }, ref) {
  const [schema,            setSchema]            = useState(null);
  const [initialValues,     setInitialValues]     = useState(null);
  const [prefillData,       setPrefillData]       = useState(null);
  const [providerOverrides, setProviderOverrides] = useState({});
  const [sigOverrides,      setSigOverrides]      = useState({});   // for applySignature
  const [suppressedFieldIds, setSuppressedFieldIds] = useState([]); // fields with hideInPatientApp=true
  const [loading,           setLoading]           = useState(true);
  const [error,             setError]             = useState(null);
  const [isSaved,           setIsSaved]           = useState(false);
  // This app is used almost exclusively on phones. FormRenderer defaults to
  // desktop layout unless explicitly told otherwise, and hidePreviewToggle
  // below hides the manual Desktop/Mobile switch too — so without this,
  // fields sized 'half'/'third'/etc. never collapse to full width on a
  // narrow screen, which was contributing to the horizontal-overflow issue.
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 768 : true
  );
  useEffect(() => {
    const onResize = () => setIsMobileViewport(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const responsesRef = useRef({});
  const loadedKeyRef = useRef(null);
  const onReadyRef   = useRef(onReady);
  onReadyRef.current = onReady;

  const formTemplateId = Math.abs(Number(String(tabId).replace("fb_", "")));
  const { patientData } = usePatientData();
  const pd = patientData || {};

  // If patientData loads later, update case_type in prefillData without re-fetching
  useEffect(() => {
    if (!pd?.caseTypeId) return;
    const caseTypeName = getCaseTypeNameById(pd.caseTypeId);
    if (caseTypeName) {
      setPrefillData(prev => (prev && !prev.case_type ? { ...prev, case_type: caseTypeName } : prev));
    }
  }, [pd?.caseTypeId]);

  // Auto-apply copied patient signature when copySignature is turned on or schema loads
  useEffect(() => {
    if (!copySignature || !schema) return;
    const storedSig = sessionStorage.getItem('sharedPatientSignature');
    if (!storedSig) return;
    let fields = [];
    try { fields = JSON.parse(schema.schemaJson || '{}').fields || []; } catch {}
    const overrides = {};
    fields.forEach(f => { if (f.type === 'sig_patient') overrides[f.id] = storedSig; });
    if (Object.keys(overrides).length) {
      setSigOverrides(prev => ({ ...prev, ...overrides }));
    }
  }, [copySignature, schema]);

  useEffect(() => {
    let cancelled = false;
    const currentKey = `${formTemplateId}::${tabId}`;
    if (loadedKeyRef.current === currentKey) {
      return;
    }

    (async () => {
      try {
        setLoading(true);
        const row       = getSelectedRow();
        const td        = getTokenData();
        const caseId    = td.caseId    || td.CaseId    || "";
        const companyId = td.companyId || td.CompanyId || "";
        const token     = getCommonServiceToken();
        const baseUrl   = row?.object?.commonServiceUrl;

        if (!baseUrl) {
          if (!cancelled) setError("Configuration error: service URL not found.");
          return;
        }
        if (!token) {
          if (!cancelled) setError("Session expired. Please reopen the link.");
          return;
        }

        const caseTypeName = getCaseTypeNameById(pd?.caseTypeId);

        // Fetch everything in parallel — schema, saved data, AND prefill data
        const [schemaData, savedValues, prefill] = await Promise.all([
          apiFetchSchema(baseUrl, formTemplateId, companyId, token),
          apiFetchSaved(baseUrl, formTemplateId, caseId, companyId, token),
          buildPrefillData(caseTypeName),
        ]);

        if (cancelled) return;

        if (!schemaData) {
          setError("Could not load form. Please try again.");
          return;
        }

        loadedKeyRef.current = currentKey;

        // ── Pre-select provider_dropdown — same as Intakesheetwrapper lines 82-90 ──
        let schemaFields = [];
        try { schemaFields = JSON.parse(schemaData.schemaJson || "{}").fields || []; } catch {}
        const ddlOverride = {};
        schemaFields.forEach(f => {
          if (f.type === "provider_dropdown" && prefill?.provider_id) {
            ddlOverride[f.id] = prefill.provider_id;
          }
        });

        setSchema(schemaData);
        setInitialValues(savedValues);
        setPrefillData(prefill);
        if (Object.keys(ddlOverride).length) {
          setProviderOverrides({ ...ddlOverride, _ts: Date.now() });
        }
        setIsSaved(Object.keys(savedValues).length > 0);

        // Compute which signature field IDs have hideInPatientApp=true
        // so FormRenderer knows to suppress them in the patient app.
        const hiddenIds = schemaFields
          .filter(f => f.hideInPatientApp === true)
          .map(f => f.id);
        if (hiddenIds.length) setSuppressedFieldIds(hiddenIds);

        // Auto-apply copied patient signature if available (mirrors legacy AOB copySignature logic)
        const storedSig = sessionStorage.getItem('sharedPatientSignature');
        if (copySignature && storedSig) {
          let fields = [];
          try { fields = JSON.parse(schemaData.schemaJson || '{}').fields || []; } catch {}
          const overrides = {};
          fields.forEach(f => { if (f.type === 'sig_patient') overrides[f.id] = storedSig; });
          if (Object.keys(overrides).length) setSigOverrides(overrides);
        }
      } catch (e) {
        if (!cancelled) {
          console.error("[DynamicIntakeTab] load error:", e);
          setError("Failed to load form.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          onReadyRef.current?.(tabId);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [formTemplateId, tabId]); // eslint-disable-line react-hooks/exhaustive-deps

  useImperativeHandle(ref, () => ({
    save: async () => {
      try {
        const row         = getSelectedRow();
        const td          = getTokenData();
        const caseId      = td.caseId    || td.CaseId    || "";
        const companyId   = td.companyId || td.CompanyId || "";
        const userId      = td.userId    || td.UserId    || td.ID || "";
        const token       = getCommonServiceToken();
        const baseUrl     = row?.object?.commonServiceUrl;
        const formName    = schema?.formTemplateName || schema?.name || "";
        const formDataObj = buildFormDataObj(schema, responsesRef.current);

        const ok = await apiSave(baseUrl, token, formTemplateId, caseId, companyId, userId, formName, formDataObj);
        if (ok) { toast.success("Form saved."); setIsSaved(true); return true; }
        else    { toast.error("Failed to save form."); return false; }
      } catch (e) {
        console.error("[DynamicIntakeTab] save error:", e);
        toast.error("Failed to save form.");
        return false;
      }
    },
    // isPending: form is pending if patient has not signed (mirrors legacy AOB logic)
    isPending: () => {
      try {
        let schemaFields = [];
        try { schemaFields = JSON.parse(schema?.schemaJson || '{}').fields || []; } catch {}
        const sigField = schemaFields.find(f => f.type === 'sig_patient');
        if (sigField) return !responsesRef.current[sigField.id];
      } catch {}
      return !isSaved;
    },
    // getSignature: return current patient signature value (mirrors legacy getSignature)
    getSignature: () => {
      try {
        let schemaFields = [];
        try { schemaFields = JSON.parse(schema?.schemaJson || '{}').fields || []; } catch {}
        const sigField = schemaFields.find(f => f.type === 'sig_patient');
        return sigField ? (responsesRef.current[sigField.id] || '') : '';
      } catch { return ''; }
    },
    // applySignature: push a signature into all sig_patient fields (mirrors legacy applySignature)
    applySignature: (sig) => {
      if (!sig) return;
      try {
        let schemaFields = [];
        try { schemaFields = JSON.parse(schema?.schemaJson || '{}').fields || []; } catch {}
        const overrides = {};
        schemaFields.forEach(f => { if (f.type === 'sig_patient') overrides[f.id] = sig; });
        if (Object.keys(overrides).length) {
          // `_force` makes FormRenderer re-apply even when this is the same image it
          // already applied once (e.g. the patient cleared the box after the first copy).
          setSigOverrides(prev => ({ ...prev, ...overrides, _force: Date.now() }));
        }
      } catch {}
    },
    print: () => {},
  }), [isSaved, schema, formTemplateId]); // eslint-disable-line

  if (loading) return (
    <div style={{ padding: 40, textAlign: "center", color: "#7d6e61", fontSize: 14 }}>⏳ Loading form…</div>
  );
  if (error) return (
    <div style={{ padding: 40, textAlign: "center", color: "#dc3545", fontSize: 14 }}>{error}</div>
  );

  // ── CRITICAL: don't render FormRenderer until prefillData is ready ────────
  // This mirrors how Intakesheetwrapper works — prefillData comes from fetchPatient
  // and FormRenderer only mounts once it's available, so useState() seeds correctly
  if (!prefillData || !initialValues) return (
    <div style={{ padding: 40, textAlign: "center", color: "#7d6e61", fontSize: 14 }}>⏳ Loading data…</div>
  );

  return (
    <div style={{ padding: "0 0 24px 0" }}>
      <FormRenderer
        schema={schema}
        prefillData={prefillData}
        initialValues={initialValues}
        valueOverrides={{...providerOverrides, ...sigOverrides}}
        disabledPrefilled={true}
        hideSaveButtons={true}
        hideTopbar={true}
        hidePreviewToggle={true}
        mobile={isMobileViewport}
        suppressedFieldIds={suppressedFieldIds}
        onChange={responses => { responsesRef.current = responses; }}
      />
    </div>
  );
});

export default DynamicIntakeTab;